/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type OptionsService from '@linc-technologies/linced-common/services/options';
import type StoreService from '@linc-technologies/linced-common/services/store';
import {
  filterBy as filterByArray,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';
import AdministeredBy from 'linced-app/enums/medical/administered-by';
import AutoinjectorBrand from 'linced-app/enums/medical/autoinjector-brand';
import MedicationType from 'linced-app/enums/medical/medication-type';
import Unit from 'linced-app/enums/medical/unit';
import type Batch from 'linced-app/models/medical/batch';
import type Schedule from 'linced-app/models/medical/schedule';
import type MedicalCondition from 'linced-app/models/people/medical-condition';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';
import type Location from '@linc-technologies/linced-common/models/schools/location';

const autoinjectorTypes: number[] = [
  MedicationType.AUTOINJECTOR___STANDARD,
  MedicationType.AUTOINJECTOR___JUNIOR,
];

// Proto: medical.Medication.
export default class Medication extends Model {
  declare [Type]: 'medical/medication';

  @service
  declare options: OptionsService;

  @service
  declare store: StoreService;

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the school the medication is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // type is the type of the medication.
  @attr('number', { defaultValue: 0 })
  declare type: number;

  // autoinjectorBrand is the brand of the medication if it is an autoinjector
  @attr('number', { defaultValue: 0 })
  declare autoinjectorBrand: number;

  // shortDescription is a short description of the medication
  @attr('string', { defaultValue: '' })
  declare shortDescription: string;

  // longDescription is a long description of the medication
  @attr('string', { defaultValue: '' })
  declare longDescription: string;

  // locationId is the location of the medication
  @belongsTo('schools/location', { inverse: null, async: false })
  declare locationId: Location | null;

  @alias('locationId')
  declare location: Location | null;

  // personId is code student the medication is associated with
  // No personId indicates the medication is owned by the school
  @belongsTo('person', { async: true, inverse: 'medications' })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  // allowSiblingUse indicates if the medication can be used by a sibling
  @attr('boolean', { defaultValue: false })
  declare allowSiblingUse: boolean;

  // administeredBy indicates who the medication can be administered by
  @attr('number', { defaultValue: 0 })
  declare administeredBy: number;

  // administrationDetails is the administration details of the medication
  @attr('string', { defaultValue: '' })
  declare administrationDetails: string;

  // dosage is indicated dosage of the medication
  // This can be overruled by a scheduled dosage
  @attr('string', { defaultValue: '' })
  declare dosage: string;

  // unit is the unit of measurement for the medication
  // This relates to the quantity of the associated batches
  @attr('number', { defaultValue: 0 })
  declare unit: number;

  // authorisedStaffIds is a list of ids of staff authorised to
  // administer this medication
  // No inverse: person.medications is the patient's own medication list and
  // pairs with personId below. There is no reciprocal "medications I may
  // administer" field on person, so this side stands alone -- claiming
  // person.medications here would give it two inverses and make EmberData
  // resolve personId as a hasMany.
  @hasMany('person', { async: true, inverse: null })
  declare authorisedStaffIds: AsyncHasMany<Person>;

  @alias('authorisedStaffIds')
  declare authorisedStaff: AsyncHasMany<Person>;

  // deleted is used to implement soft-deletes
  @attr('boolean', { defaultValue: false })
  declare deleted: boolean;

  @hasMany('medical/batch', { async: true, inverse: 'medicationId' })
  declare batches: AsyncHasMany<Batch>;

  // batches is a PromiseManyArray; classic filterBy's A()-based
  // implementation is deprecated on it, so resolve to the loaded records
  // first.
  @computed('batches.content.@each.deleted')
  get nonDeletedBatches() {
    return filterByArray(resolved(this.batches), 'deleted', false);
  }

  @filterBy('nonDeletedBatches', 'quantity')
  declare nonEmptyBatches: unknown[];

  @hasMany('medical/schedule', { async: true, inverse: 'medicationId' })
  declare schedules: AsyncHasMany<Schedule>;

  // See nonDeletedBatches above: schedules is also a PromiseManyArray.
  @computed('schedules.content.@each.deleted')
  get nonDeletedSchedules() {
    return filterByArray(resolved(this.schedules), 'deleted', false);
  }

  @computed('type')
  get typeString() {
    if (this.type) {
      return MedicationType.getLabel(this.type);
    }
    return '';
  }

  @computed('autoinjectorBrand')
  get autoinjectorBrandString() {
    if (this.autoinjectorBrand) {
      return AutoinjectorBrand.getLabel(this.autoinjectorBrand);
    }
    return '';
  }

  @computed('nonDeletedBatches.@each.quantity')
  get quantity() {
    return this.nonDeletedBatches.reduce((count, batch) => {
      // Ember can do funny things with the value of a number input
      return count + parseFloat(batch.get('quantity') as unknown as string);
    }, 0);
  }

  @computed('nonDeletedBatches.@each.expiryDate')
  get latestExpiringBatch() {
    if (this.nonDeletedBatches.length === 0) {
      return null;
    }

    return this.nonDeletedBatches.reduce((max, batch) => {
      if (!max) return batch;
      // TS-ZERO(expiryDate)
      return batch.get('expiryDate')! > max.get('expiryDate')! ? batch : max;
    }, this.nonDeletedBatches[0]);
  }

  @computed('person.fullName')
  get ownedBy() {
    if (!isEmpty(this.person_id)) {
      return this.person.get('fullName');
    }
    return 'School';
  }

  @computed('person.homeroom.label')
  get room() {
    if (!isEmpty(this.person_id)) {
      return this.person.get('homeroom.label');
    }
    return '';
  }

  @computed('allowSiblingUse')
  get allowSiblingUseString() {
    return this.allowSiblingUse ? 'Yes' : 'No';
  }

  @computed('administeredBy')
  get administeredByString() {
    if (this.administeredBy) {
      return AdministeredBy.getLabel(this.administeredBy);
    }
    return '';
  }

  @computed('type')
  get isAutoinjector() {
    return autoinjectorTypes.includes(this.type);
  }

  @computed('unit')
  get unitString() {
    if (this.unit) {
      return Unit.getLabel(this.unit);
    }
    return '';
  }

  @computed('person.learner.medicalConditions.[]')
  get medicalCondition(): MedicalCondition | undefined {
    if (this.person_id) {
      return (
        (this.person.get('learner.medicalConditions') ??
          []) as MedicalCondition[]
      ).find((medicalCondition) => {
        return medicalCondition.medicationIds.includes(this.id!);
      });
    }
  }

  @computed(
    'type',
    'isAutoinjector',
    'autoinjectorBrand',
    'location',
    'shortDescription',
    'administeredBy',
    'dosage',
    'unit',
  )
  get isValid() {
    if (!this.type) {
      return false;
    }
    if (this.isAutoinjector && !this.autoinjectorBrand) {
      return false;
    }
    if (!this.location) {
      return false;
    }
    if (isEmpty(this.shortDescription)) {
      return false;
    }
    if (!this.administeredBy) {
      return false;
    }
    if (isEmpty(this.dosage)) {
      return false;
    }
    if (!this.unit) {
      return false;
    }
    return true;
  }
}
